import { Backdrop, CircularProgress, Stack } from "@mui/material";
import moment from "moment";
import "moment/locale/tr";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditSummary } from "../../Models/Credit";
import { ROUTE_PATHS } from "../../Routes/constants";
import { masterpassRecurringInit } from "../../Services/BankService";
import { BulkSnoozeDatesResponse } from "../../Services/SnoozeService";
import { sendEvent } from "../../Utils/analytics";
import {
  BORDER_LIGHT,
  CARD_SHADOW,
  INK,
  TEXT_MUTED,
} from "../../Utils/designTokens";
import { getRemoteText, getRemoteValue } from "../../Utils/firebase";
import useAuthState from "../../Utils/store";
import CLoadingButton from "../CLoadingButton/CLoadingButton";
import CSkeleton from "../CSkeleton/CSkeleton";
import { FormatAmountSymbol } from "../FormatData/FormatAmount";
import OverdueSummaryChip from "./OverdueSummaryChip";

interface Props {
  totalDebtsData?: CreditSummary;
  snoozeAvailability?: BulkSnoozeDatesResponse;
  onBulkSnoozeClick: () => void;
  /** CRL-3241: opens the bulk pay modal (Tümü / Tutar gir) before checkout. */
  onBulkPayClick: () => void;
}

const cardSx = {
  width: "100%",
  borderRadius: "20px",
  border: `1px solid ${BORDER_LIGHT}`,
  background: "#FFF",
  boxShadow: CARD_SHADOW,
  padding: "20px",
} as const;

/**
 * CRL-3241: mobile summary cards are stacked vertically (mobile mocks)
 * instead of the old swiper; every card/button/condition is preserved.
 */
const MobileTotalDebts = ({ totalDebtsData, onBulkSnoozeClick, onBulkPayClick, snoozeAvailability }: Props) => {
  const [loading, setLoading] = useState(false);
  const user = useAuthState((state) => state.user);
  const navigate = useNavigate();
  // summary response is authoritative; the jwt property can be stale, so it is only a fallback until the summary is fetched
  const jwtAutoRepaymentStatus = user?.properties.find((p) => p.key === "masterpassAutoRepaymentEnabled")?.status;
  // assume enabled while neither source has a value yet, so the setup card does not flash during loading
  const masterpassAutoRepaymentEnabled = totalDebtsData?.masterpassAutoRepaymentEnabled ??
    (jwtAutoRepaymentStatus != undefined ? jwtAutoRepaymentStatus == 1 : true);
  const recurringCompleted = useAuthState((state) => state.isRecurringCompleted);
  const payments = useAuthState((state) => state.payments);

  /** Nearest due date among the loaded pending payments (design: "en yakın vade"). */
  const nearestDueDate = useMemo(() => {
    const dates = (payments ?? [])
      .map((p) => new Date(p.dueDate).getTime())
      .filter((t) => !Number.isNaN(t));
    if (dates.length === 0) return null;
    return new Date(Math.min(...dates));
  }, [payments]);

  const summarySubtitle = useMemo(() => {
    if (!totalDebtsData || totalDebtsData.activeCreditCount <= 0) return null;
    const countText = getRemoteText(
      "payments_count_x",
      "{val} ödeme",
      totalDebtsData.activeCreditCount.toString()
    );
    if (!nearestDueDate) return countText;
    const dateText = getRemoteText(
      "nearest_due_x",
      "en yakın vade {val}",
      moment(nearestDueDate).locale("tr").format("D MMMM YYYY")
    );
    return `${countText} · ${dateText}`;
  }, [totalDebtsData, nearestDueDate]);

  const handleMasterpassRecurringRedirect = async () => {
    sendEvent(`click_recurring_masterpass_button`);
    setLoading(true);
    const recurringResponse = await masterpassRecurringInit(
      user?.phoneNumber || '',
    ).finally(() => {
      setLoading(false);
    });
    if (recurringResponse) {
      navigate(ROUTE_PATHS.MASTERPASS_IFRAME, { state: { response: recurringResponse, referrer: ROUTE_PATHS.DEFAULT_PAYMENTS } });
    }
  };

  const isSnoozeAvailabilityLoaded = (): boolean => snoozeAvailability != undefined || false;

  return (
    <>
      <Stack pt={3} gap={"16px"}>
        {/* Toplam borç */}
        <Stack sx={cardSx} gap={"20px"}>
          <Stack>
            {totalDebtsData?.totalAmount !== undefined ? (
              <>
                <p className="text-[14px] font-medium" style={{ color: TEXT_MUTED }}>
                  {getRemoteValue("total_debt_amount")}
                </p>
                <p className="text-[28px] font-extrabold leading-tight pt-1" style={{ color: INK }}>
                  {FormatAmountSymbol(totalDebtsData?.totalAmount)}
                </p>
                {summarySubtitle && (
                  <p className="text-[13px] pt-1" style={{ color: TEXT_MUTED }}>
                    {summarySubtitle}
                  </p>
                )}
                <OverdueSummaryChip
                  count={totalDebtsData.lateCreditCount}
                  amount={totalDebtsData.totalLateAmount}
                />
              </>
            ) : (
              <>
                <CSkeleton width={150} height={30} />
                <CSkeleton width={200} height={44} />
              </>
            )}
          </Stack>
          <Stack direction={"row"} gap={"12px"}>
            {isSnoozeAvailabilityLoaded() ? (
              <CLoadingButton
                disabled={snoozeAvailability == undefined || snoozeAvailability == null || snoozeAvailability.isSnoozeAvailable === false}
                loading={!isSnoozeAvailabilityLoaded()}
                onClick={onBulkSnoozeClick}
                fullWidth
                size="large"
                variant="outlined"
              >
                {getRemoteValue("snooze_all_debts")}
              </CLoadingButton>
            ) : (
              <CSkeleton width={150} height={48} />
            )}
            {totalDebtsData?.totalAmount !== undefined ? (
              <CLoadingButton
                fullWidth
                size="large"
                disabled={totalDebtsData?.totalAmount === 0}
                variant="contained"
                onClick={() => {
                  sendEvent(`click_pay_all_payment_button`, {
                    total_debt_amount: totalDebtsData?.totalAmount,
                  });
                  onBulkPayClick();
                }}
              >
                {getRemoteValue("pay_all_debts")}
              </CLoadingButton>
            ) : (
              <CSkeleton width={150} height={48} />
            )}
          </Stack>
        </Stack>


        {/* Otomatik Ödeme Talimatı Ver */}
        {!masterpassAutoRepaymentEnabled && (
          <Stack sx={cardSx} gap={"16px"}>
            <Stack>
              <p className="text-[16px] font-bold" style={{ color: INK }}>
                {getRemoteValue("recurring_payment_label")}
              </p>
              <p className="text-[13px] font-normal pt-1" style={{ color: TEXT_MUTED }}>
                {getRemoteValue("recurring_payment_desc")}
              </p>
            </Stack>
            <CLoadingButton
              fullWidth
              size="large"
              variant="outlined"
              loading={loading}
              onClick={() => {
                handleMasterpassRecurringRedirect();
              }}
            >
              {getRemoteValue("recurring_payment_button")}
            </CLoadingButton>
          </Stack>
        )}
      </Stack>
      <Backdrop
        open={loading}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 1,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default MobileTotalDebts;
